import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { MealPhotoQueueResponse } from '@/api/generated/MNT';
import { type CoreRegionalApis } from '@/apiClients/coreRegionalApis';
import { ApiRegion, config } from '@/config';
import { useFeatures } from '@/context/FeatureContext';
import { RegionScope, useRegionalApis } from '@/context/RegionScope';
import { useInterval } from '@/hooks/useInterval';
import { useMealQueueService } from '@/services/MealQueueService';

export type CombinedQueueItem = MealPhotoQueueResponse & { region: ApiRegion; regionalKey: string };

type RegionQueueState = {
  items: MealPhotoQueueResponse[];
  loading: boolean;
  error: boolean;
  apis: CoreRegionalApis;
  refetch: (reason: string) => void;
};

type CombinedQueueState = {
  items: CombinedQueueItem[];
  loading: boolean;
  partialFailure: boolean;
  regions: Partial<Record<ApiRegion, RegionQueueState>>;
  refetch: (reason: string) => void;
};

const CombinedQueueContext = React.createContext<CombinedQueueState | null>(null);

export const mergeQueueItems = (canada: MealPhotoQueueResponse[], us: MealPhotoQueueResponse[]): CombinedQueueItem[] =>
  [
    ...canada.map(item => ({ ...item, region: ApiRegion.CA, regionalKey: `${ApiRegion.CA}:${item.id}` })),
    ...us.map(item => ({ ...item, region: ApiRegion.US, regionalKey: `${ApiRegion.US}:${item.id}` })),
  ].sort(
    (a, b) => Date.parse(a.created_time) - Date.parse(b.created_time) || a.regionalKey.localeCompare(b.regionalKey),
  );

const RegionQueueReader = ({ onChange }: { onChange: (region: ApiRegion, state: RegionQueueState) => void }) => {
  const apis = useRegionalApis();
  const { queue_polling: queuePolling } = useFeatures();
  const queue = useMealQueueService();
  const latestRefetch = useRef(queue.refetch);
  useLayoutEffect(() => {
    latestRefetch.current = queue.refetch;
  });
  const refetch = useCallback((reason: string) => latestRefetch.current(reason), []);
  useInterval(() => queue.refetch('interval:us-poll'), queuePolling && apis.region === ApiRegion.US ? 30_000 : null);

  useEffect(() => {
    onChange(apis.region, {
      items: queue.query.isError ? [] : queue.allActiveQueues,
      loading: queue.query.isPending,
      error: queue.query.isError,
      apis,
      refetch,
    });
  }, [apis, onChange, queue.allActiveQueues, queue.query.isPending, queue.query.isError, refetch]);
  return null;
};

export const CombinedQueueProvider = ({ children }: { children: React.ReactNode }) => {
  const [regions, setRegions] = useState<Partial<Record<ApiRegion, RegionQueueState>>>({});
  const onChange = useCallback((region: ApiRegion, state: RegionQueueState) => {
    setRegions(previous => {
      const old = previous[region];
      if (
        old?.items.length === state.items.length
        && old.items.every((item, index) => item === state.items[index])
        && old.loading === state.loading
        && old.error === state.error
        && old.apis === state.apis
        && old.refetch === state.refetch
      ) {
        return previous;
      }
      return { ...previous, [region]: state };
    });
  }, []);
  const value = useMemo<CombinedQueueState>(
    () => ({
      items: mergeQueueItems(regions[ApiRegion.CA]?.items ?? [], regions[ApiRegion.US]?.items ?? []),
      loading:
        !regions[ApiRegion.CA]
        || (!config.usRegionalMutationsBlocked && !regions[ApiRegion.US])
        || Object.values(regions).some(region => region.loading),
      partialFailure: config.usRegionalMutationsBlocked || Object.values(regions).some(region => region.error),
      regions,
      refetch: (reason: string) => Object.values(regions).forEach(region => region.refetch(reason)),
    }),
    [regions],
  );

  return (
    <CombinedQueueContext.Provider value={value}>
      <RegionScope region={ApiRegion.CA}>
        <RegionQueueReader onChange={onChange} />
      </RegionScope>
      {!config.usRegionalMutationsBlocked && (
        <RegionScope region={ApiRegion.US} showAlerts={false}>
          <RegionQueueReader onChange={onChange} />
        </RegionScope>
      )}
      {children}
    </CombinedQueueContext.Provider>
  );
};

export const useCombinedQueue = (): CombinedQueueState => {
  const value = useContext(CombinedQueueContext);
  if (!value) {
    throw new Error('useCombinedQueue requires CombinedQueueProvider');
  }
  return value;
};
